'use client';

import { useWebRTC } from '@/contexts/WebRTCContext';

export default function ReconnectToastContainer() {
  const { toasts, dismissToast } = useWebRTC();

  if (toasts?.length === 0) return null;

  return (
    <div
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none"
    >
      {toasts?.map((toast) => (
        <div
          key={toast?.id}
          className={`
            pointer-events-auto flex items-start gap-3 rounded-xl px-4 py-3 shadow-2xl
            border backdrop-blur-sm min-w-[280px] max-w-[340px]
            ${toast?.failed
              ? 'bg-red-950/90 border-red-700/60 text-red-100' :'bg-gray-900/95 border-yellow-500/40 text-gray-100'
            }
            animate-slide-in
          `}
        >
          {/* Icon */}
          <div className="mt-0.5 shrink-0">
            {toast?.failed ? (
              <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-yellow-400 animate-spin-slow" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight">
              {toast?.failed ? `${toast?.cameraLabel} — Connection Failed` : `${toast?.cameraLabel} — Reconnecting`}
            </p>
            <p className={`text-xs mt-0.5 ${toast?.failed ? 'text-red-300' : 'text-gray-400'}`}>
              {toast?.failed
                ? `Could not reconnect after ${toast?.maxAttempts} attempts`
                : toast?.secondsLeft > 0
                  ? `Reconnecting in ${toast?.secondsLeft}s… (attempt ${toast?.attempt}/${toast?.maxAttempts})`
                  : `Attempting to reconnect… (attempt ${toast?.attempt}/${toast?.maxAttempts})`
              }
            </p>

            {/* Progress bar for countdown */}
            {!toast?.failed && toast?.secondsLeft > 0 && (
              <div className="mt-2 h-1 w-full rounded-full bg-gray-700 overflow-hidden">
                <div
                  className="h-full rounded-full bg-yellow-400 transition-all duration-1000 ease-linear"
                  style={{
                    width: `${100 - (toast?.secondsLeft / Math.min(Math.pow(2, toast?.attempt - 1), 30)) * 100}%`,
                  }}
                />
              </div>
            )}
          </div>

          {/* Dismiss button */}
          <button
            onClick={() => dismissToast(toast?.id)}
            className="shrink-0 mt-0.5 text-gray-500 hover:text-gray-300 transition-colors"
            aria-label="Dismiss notification"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}
